import React, { useEffect, useRef, useState } from 'react';
import styles from './PromotionsPage.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// The uploaded image URLs come back relative ("/api/uploads/..."), so resolve
// them against the API origin rather than the admin's own host.
const API_ORIGIN = API.replace(/\/api\/?$/, '');

function authHeaders(json = true) {
  const h = { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const resolveImage = (url) =>
  !url ? '' : /^https?:\/\//i.test(url) ? url : `${API_ORIGIN}${url}`;

const EMPTY = {
  title: '', subtitle: '', imageUrl: '', linkUrl: '',
  sortOrder: 0, isActive: true, startsAt: '', endsAt: '',
};

export default function PromotionsPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []);

  function flash(setter, text) {
    setter(text);
    setTimeout(() => setter(''), 4000);
  }

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/promotions`, { headers: authHeaders() });
      const data = await res.json();
      setPromos(data.promotions || []);
    } catch {
      flash(setErr, 'Could not load promotions.');
    } finally {
      setLoading(false);
    }
  }

  function openNew() {
    setForm({ ...EMPTY, sortOrder: promos.length });
    setEditing({});
  }

  function openEdit(p) {
    setForm({
      title: p.title || '',
      subtitle: p.subtitle || '',
      imageUrl: p.imageUrl || '',
      linkUrl: p.linkUrl || '',
      sortOrder: p.sortOrder ?? 0,
      isActive: !!p.isActive,
      startsAt: p.startsAt ? p.startsAt.slice(0, 10) : '',
      endsAt: p.endsAt ? p.endsAt.slice(0, 10) : '',
    });
    setEditing(p);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      flash(setErr, 'That image is larger than 5 MB. Please pick a smaller one.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setErr('');
    try {
      const body = new FormData();
      body.append('image', file);
      const res = await fetch(`${API}/admin/promotions/upload`, {
        method: 'POST',
        headers: authHeaders(false), // let the browser set the multipart boundary
        body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Upload failed.');
      setForm((f) => ({ ...f, imageUrl: data.url }));
      flash(setMsg, 'Image uploaded.');
    } catch (e2) {
      flash(setErr, e2.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function save() {
    if (!form.imageUrl) return flash(setErr, 'Please upload an image first.');
    if (!form.linkUrl.trim()) return flash(setErr, 'Please enter the collection URL this slide opens.');
    if (!/^https?:\/\//i.test(form.linkUrl.trim())) {
      return flash(setErr, 'The link must start with http:// or https://');
    }

    setSaving(true);
    setErr('');
    try {
      const isNew = !editing?.id;
      const res = await fetch(
        isNew ? `${API}/admin/promotions` : `${API}/admin/promotions/${editing.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            ...form,
            sortOrder: Number(form.sortOrder) || 0,
            startsAt: form.startsAt || null,
            endsAt: form.endsAt || null,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not save.');
      setEditing(null);
      flash(setMsg, isNew ? 'Promotion added.' : 'Promotion updated.');
      load();
    } catch (e) {
      flash(setErr, e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(p) {
    try {
      await fetch(`${API}/admin/promotions/${p.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ isActive: !p.isActive }),
      });
      load();
    } catch {
      flash(setErr, 'Could not update that promotion.');
    }
  }

  async function move(index, direction) {
    const next = [...promos];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPromos(next); // optimistic — the reorder call is cheap and idempotent
    try {
      await fetch(`${API}/admin/promotions/reorder`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ order: next.map((p, i) => ({ id: p.id, sortOrder: i })) }),
      });
    } catch {
      flash(setErr, 'Could not save the new order.');
      load();
    }
  }

  async function remove(p) {
    if (!window.confirm(`Delete "${p.title || 'this promotion'}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/admin/promotions/${p.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      flash(setMsg, 'Promotion deleted.');
      load();
    } catch {
      flash(setErr, 'Could not delete that promotion.');
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Promotions</h1>
          <p className={styles.subtitle}>
            The slider on the app's home screen. Each slide opens a collection on the website.
          </p>
        </div>
        <button className={styles.primaryBtn} onClick={openNew}>+ New promotion</button>
      </div>

      {msg && <div className={styles.toastOk}>{msg}</div>}
      {err && <div className={styles.toastErr}>{err}</div>}

      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : promos.length === 0 ? (
        <div className={styles.empty}>
          <p>No promotions yet.</p>
          <p className={styles.emptyHint}>
            The app hides the slider entirely until you add one, so nothing looks broken.
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {promos.map((p, i) => (
            <div key={p.id} className={`${styles.card} ${!p.isActive ? styles.cardInactive : ''}`}>
              <div className={styles.thumbWrap}>
                {p.imageUrl
                  ? <img src={resolveImage(p.imageUrl)} alt={p.title || 'Promotion'} className={styles.thumb} />
                  : <div className={styles.thumbEmpty}>No image</div>}
              </div>

              <div className={styles.cardBody}>
                <div className={styles.cardTitleRow}>
                  <span className={styles.cardTitle}>{p.title || <em>Untitled</em>}</span>
                  <span className={p.isActive ? styles.pillOn : styles.pillOff}>
                    {p.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>
                {p.subtitle && <div className={styles.cardSub}>{p.subtitle}</div>}
                <a className={styles.cardLink} href={p.linkUrl} target="_blank" rel="noreferrer">
                  {p.linkUrl}
                </a>
                {(p.startsAt || p.endsAt) && (
                  <div className={styles.cardDates}>
                    {p.startsAt ? `From ${p.startsAt.slice(0, 10)}` : 'From now'}
                    {' · '}
                    {p.endsAt ? `until ${p.endsAt.slice(0, 10)}` : 'no end date'}
                  </div>
                )}
              </div>

              <div className={styles.actions}>
                <button className={styles.iconBtn} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
                <button className={styles.iconBtn} onClick={() => move(i, 1)} disabled={i === promos.length - 1} title="Move down">↓</button>
                <button className={styles.ghostBtn} onClick={() => toggleActive(p)}>
                  {p.isActive ? 'Hide' : 'Show'}
                </button>
                <button className={styles.ghostBtn} onClick={() => openEdit(p)}>Edit</button>
                <button className={styles.dangerBtn} onClick={() => remove(p)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className={styles.modalOverlay} onClick={() => !saving && setEditing(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>
              {editing.id ? 'Edit promotion' : 'New promotion'}
            </h2>

            <label className={styles.label}>Image *</label>
            <div className={styles.uploadRow}>
              {form.imageUrl && (
                <img src={resolveImage(form.imageUrl)} alt="Preview" className={styles.preview} />
              )}
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleUpload}
                  disabled={uploading}
                />
                <div className={styles.hint}>
                  JPG, PNG or WebP · up to 5 MB · around 1200×600 works best
                </div>
                {uploading && <div className={styles.hint}>Uploading…</div>}
              </div>
            </div>

            <label className={styles.label}>Link URL *</label>
            <input
              className={styles.input}
              value={form.linkUrl}
              onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
              placeholder="https://thetoystorelb.com/collections/new-arrivals"
            />

            <label className={styles.label}>Title</label>
            <input
              className={styles.input}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="New Arrivals"
              maxLength={200}
            />

            <label className={styles.label}>Subtitle</label>
            <input
              className={styles.input}
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="Fresh in this week"
              maxLength={300}
            />

            <div className={styles.row}>
              <div style={{ flex: 1 }}>
                <label className={styles.label}>Starts (optional)</label>
                <input
                  className={styles.input}
                  type="date"
                  value={form.startsAt}
                  onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className={styles.label}>Ends (optional)</label>
                <input
                  className={styles.input}
                  type="date"
                  value={form.endsAt}
                  onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                />
              </div>
            </div>

            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <span>Show this promotion in the app</span>
            </label>

            <div className={styles.modalActions}>
              <button className={styles.ghostBtn} onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button className={styles.primaryBtn} onClick={save} disabled={saving || uploading}>
                {saving ? 'Saving…' : editing.id ? 'Save changes' : 'Add promotion'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
